import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  type ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { PhotoService } from '@core/services/photo.service';

import { Dynamic, type DynamicAction } from '@shared/registry/dynamic';

import type { CaptureCardConfig } from './capture-card.types';

@Component({
  selector: 'app-capture-card',
  templateUrl: './capture-card.html',
  imports: [Dynamic],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block flex-1' },
  providers: [PhotoService],
})
export class CaptureCard {
  readonly config = input.required<CaptureCardConfig>();
  readonly disabled = input<boolean>(false);

  readonly action = output<DynamicAction>();

  protected readonly capturedImage = signal<string | null>(null);
  protected readonly previewSize = signal<{ width: number; height: number } | null>(null);

  private readonly cameraPreview = viewChild<ElementRef>('cameraPreview');
  private readonly photoService = inject(PhotoService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    afterRenderEffect(() => {
      if (this.cameraPreview()) {
        this.photoService.openCamera(this.config().cameraPreviewId).catch(console.error);
      }
    });

    this.destroyRef.onDestroy(() => {
      this.photoService.closeCamera().catch(console.error);
    });
  }

  protected async onCapture(): Promise<void> {
    if (this.disabled()) return;

    this.capturedImage.set(null);
    this.previewSize.set(null);
    const actionConfig = this.config().action;

    try {
      const image = await this.photoService.captureCamera();
      this.previewSize.set(this.photoService.previewSize);
      await this.photoService.closeCamera();
      this.capturedImage.set(image);
      this.action.emit({ id: actionConfig?.id || 'capture', data: { ...(actionConfig?.data as object), image } });
    } catch {
      this.capturedImage.set(null);
      await this.photoService.openCamera(this.config().cameraPreviewId);
    }
  }
}
